import { Controller } from "@hotwired/stimulus"

// Keeps the gallery limit message, dropzone visibility, and heading lock in sync with
// the current tier limit and live image count. Message copy comes from
// Things::GalleryLimitMessages via messagesValue ({limit} is substituted here) so the
// wording lives only in Ruby. Used on the admin and owner thing edit pages.
export default class extends Controller {
  static targets = ["message", "galleryDropzone", "galleryUploads", "tierSelect", "galleryTierLock", "galleryHeading"]
  static values = {
    currentTierId: String,
    tierLimits: { type: Object, default: {} },
    adminTierBypass: { type: Boolean, default: false },
    messages: { type: Object, default: {} }
  }

  connect() {
    this.updateAll()
    this.boundUpdate = this.updateAll.bind(this)
    this.element.addEventListener("gallery-images:count-changed", this.boundUpdate)
  }

  disconnect() {
    this.element.removeEventListener("gallery-images:count-changed", this.boundUpdate)
  }

  updateAll() {
    this.updateMessage()
    this.updateDropzoneVisibility()
    this.updateOverflowLock()
  }

  updateMessage() {
    if (!this.hasMessageTarget) return

    if (this.adminTierBypassValue && this.currentLimit() === 0) {
      this.messageTarget.textContent = ""
      this.messageTarget.classList.add("hidden")
      return
    }

    this.messageTarget.classList.remove("hidden")
    this.messageTarget.textContent = this.messageForLimit(this.effectiveLimit())
  }

  messageForLimit(limit) {
    const templates = this.messagesValue
    if (limit === 0) return templates.unavailable || ""

    const overflow = this.galleryImageCount() > limit
    const template = overflow ? templates.overflow : templates.withinLimit
    return (template || "").replaceAll("{limit}", String(limit))
  }

  // Mirrors GalleryImagesSectionComponent#show_gallery_tier_visual_lock? as the image
  // count changes. The "no gallery in plan" lock (raw limit 0) is static, so only the
  // overflow-driven lock toggles here. The heading dim is owner-only (admins never dim).
  updateOverflowLock() {
    if (!this.hasGalleryTierLockTarget) return

    const limit = this.currentLimit()
    if (limit === 0) return

    const overflow = this.galleryImageCount() > limit
    this.galleryTierLockTarget.classList.toggle("hidden", !overflow)

    if (this.hasGalleryHeadingTarget && !this.adminTierBypassValue) {
      this.galleryHeadingTarget.classList.toggle("tier-locked-visual", overflow)
    }
  }

  updateDropzoneVisibility() {
    if (!this.hasGalleryDropzoneTarget) return

    const limit = this.effectiveLimit()
    const count = this.galleryImageCount()

    if (limit === 0 || count >= limit) {
      this.galleryDropzoneTarget.classList.add("hidden")
    } else {
      this.galleryDropzoneTarget.classList.remove("hidden")
    }

    // Sync tier limit to image-upload controller so it enforces the cap when adding files
    const uploadEl = this.galleryDropzoneTarget.closest("[data-controller~='image-upload']")
    if (uploadEl) {
      uploadEl.dataset.imageUploadTierLimitValue = String(limit)
    }
  }

  effectiveLimit() {
    const limit = this.currentLimit()
    if (this.adminTierBypassValue && limit === 0) {
      return 100
    }

    return limit
  }

  currentLimit() {
    const tierId = this.hasTierSelectTarget ? this.tierSelectTarget.value : this.currentTierIdValue
    return tierId ? (this.tierLimitsValue[tierId] ?? 0) : 0
  }

  galleryImageCount() {
    if (!this.hasGalleryUploadsTarget) return 0
    const signed = this.galleryUploadsTarget.querySelectorAll("[data-signed-id]").length
    const pending = this.galleryUploadsTarget.querySelectorAll("[data-file-id]").length
    return signed + pending
  }
}
